import type { DimensionRow, ProcurementCategoryRow, WarehouseRow } from '../api/masterdata';
import { codeLabel } from './codeLabel';

/**
 * #147 — extracted from `RequestForm.tsx` so `ReviewerQueue.tsx`'s read-only
 * review-detail screen can resolve `procurementCategoryId`/`costCentreId`/
 * `lineOfBusinessId`/`warehouseId` to the same display labels the requester
 * saw, instead of printing the raw D365 code (e.g. `44101603` rather than
 * "Office supplies › Paper").
 */

/**
 * Looks a code up in a fetched master-data list and returns its display
 * label — falls back to the raw code when the list isn't ready yet (still
 * loading, or D365 unreachable) or the code isn't found in it, so a screen
 * never shows a blank where a code used to be, only ever an upgrade from code
 * to label once the list resolves.
 */
export function resolveDependentLabel<T>(
  state: { phase: string; rows?: T[] },
  id: string | null | undefined,
  getKey: (row: T) => string,
  getLabel: (row: T) => string,
): string {
  if (!id) return '';
  if (state.phase !== 'ready' || !state.rows) return id;
  const row = state.rows.find((candidate) => getKey(candidate) === id);
  return row ? getLabel(row) : id;
}

/** Field names are unverified against the client's D365 config pending #21 — falls back to the key alone. */
export function dimensionKey(row: DimensionRow): string {
  const value = row.DimensionValue ?? row.Value ?? row.Id;
  return typeof value === 'string' ? value : JSON.stringify(row);
}

export function dimensionLabel(row: DimensionRow): string {
  const description = row.Description ?? row.DisplayValue ?? row.Name;
  const key = dimensionKey(row);
  return codeLabel(key, typeof description === 'string' ? description : '');
}

/**
 * Categories are identified by NAME. `CategoryCode` is optional in F&O and the
 * client tenant leaves it blank on every category, so keying by code gave every
 * option the same identifier ("") — no pick could stick, and the "" it stored
 * failed draft validation. The name is present on every row, unique within a
 * hierarchy, and is what the requisition line keys on anyway. Falls back to the
 * code only for a row that genuinely has no name.
 */
export function categoryKey(row: ProcurementCategoryRow): string {
  return row.CategoryName.trim() || row.CategoryCode.trim();
}

/** `code — name` only when a real code exists; otherwise just the name (no stray leading dash). */
export function categoryLabel(row: ProcurementCategoryRow): string {
  const code = row.CategoryCode.trim();
  const name = row.CategoryName.trim();
  if (code && name && code !== name) return `${code} — ${name}`;
  return name || code;
}

/**
 * Whether a stored category value refers to this row — today's name key, or a
 * code written by drafts saved before categories were keyed by name.
 */
export function categoryMatches(
  row: ProcurementCategoryRow,
  id: string | null | undefined,
): boolean {
  if (!id) return false;
  return categoryKey(row) === id || (row.CategoryCode.trim() !== '' && row.CategoryCode === id);
}

/** Display label for a stored category value — see `resolveDependentLabel` for the fallback rules. */
export function resolveCategoryLabel(
  state: { phase: string; rows?: ProcurementCategoryRow[] },
  id: string | null | undefined,
): string {
  if (!id) return '';
  if (state.phase !== 'ready' || !state.rows) return id;
  const row = state.rows.find((candidate) => categoryMatches(candidate, id));
  return row ? categoryLabel(row) : id;
}

export function warehouseKey(row: WarehouseRow): string {
  return row.WarehouseId;
}

export function warehouseLabel(row: WarehouseRow): string {
  return `${row.WarehouseId} — ${row.WarehouseName}`;
}
